/**
 * Structured frontend logger.
 *
 * In development: writes to console only.
 * In production: writes to console AND ships to the backend /client-log endpoint
 * so logs appear in Graylog/Grafana alongside backend logs.
 *
 * Usage:
 *   import { logger, setRequestId } from '../utils/logger';
 *   logger.error('Payment failed', 'CheckoutForm', { amount, error: err.message });
 *
 * Wire up request correlation:
 *   import { setRequestId } from '../utils/logger';
 *   setRequestId(response.headers.get('X-Request-ID'));
 */

const LOG_ENDPOINT = `${import.meta.env.VITE_API_URL}/client-log`;

let currentRequestId = null;

export function setRequestId(id) {
  if (id) currentRequestId = id;
}

function send(level, message, component, fields = {}) {
  const payload = {
    level,
    message,
    request_id: currentRequestId,
    component: component || 'frontend',
    fields,
  };

  // Always write to browser console
  const consoleFn = level === 'error' ? console.error
    : level === 'warn' ? console.warn
    : level === 'debug' ? console.debug
    : console.info;
  consoleFn(`[${component}] ${message}`, fields);

  // In development, stop here
  if (import.meta.env.DEV) return;

  const body = JSON.stringify(payload);
  const blob = new Blob([body], { type: 'application/json' });

  // Use sendBeacon for error/warn — guaranteed delivery even on page unload
  if (level === 'error' || level === 'warn') {
    if (!navigator.sendBeacon(LOG_ENDPOINT, blob)) {
      // Fallback if sendBeacon returns false (e.g. size limit exceeded)
      fetch(LOG_ENDPOINT, { method: 'POST', body, headers: { 'Content-Type': 'application/json' }, keepalive: true }).catch(() => {});
    }
    return;
  }

  // info/debug: fire-and-forget fetch
  fetch(LOG_ENDPOINT, {
    method: 'POST',
    body,
    headers: { 'Content-Type': 'application/json' },
    keepalive: true,
  }).catch(() => {});
}

export const logger = {
  debug: (msg, component, fields) => send('debug', msg, component, fields),
  info:  (msg, component, fields) => send('info',  msg, component, fields),
  warn:  (msg, component, fields) => send('warn',  msg, component, fields),
  error: (msg, component, fields) => send('error', msg, component, fields),
};
